// ─────────────────────────────────────────────────────────────────
// Glass-i — Inventory API client (Stage 6P.5).
//
// Thin axios wrappers around:
//   /api/erp/inventory/*  (Inventory Inward Posting Foundation — 6P.5)
//
// Mirrors the purchaseBillApi shape — the `unwrap` helper handles both
// the {data:{data:...}} envelope used by most list/create endpoints and
// the bare {data:...} shape used by helper / status / preview routes.
//
// Backend is the source of truth for ALL inventory math:
//   posted qty, base-stock qty (sqmt/sqft conversions), valuation,
//   movement ledger entries, duplicate-block decisions.
// Frontend mirrors for instant UX but never wins.
// ─────────────────────────────────────────────────────────────────

import client from '../../../api/client';

const INV_BASE = '/api/erp/inventory';

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

export const inventoryApi = {
  // ── Dashboard (composed read-only snapshot) ───────────────────
  getDashboard:             () =>
    client.get(`${INV_BASE}/dashboard`).then(unwrap),

  // ── Stock & ledger reads ──────────────────────────────────────
  listStock:                (params = {}) =>
    client.get(`${INV_BASE}/stock`, { params }).then(unwrap),
  getStockPivot:            (params = {}) =>
    client.get(`${INV_BASE}/stock-pivot`, { params }).then(unwrap),
  getStockLedger:           (params = {}) =>
    client.get(`${INV_BASE}/stock-ledger`, { params }).then(unwrap),
  getStockSummary:          (params = {}) =>
    client.get(`${INV_BASE}/stock/summary`, { params }).then(unwrap),
  getStockByItem:           (itemId, params = {}) =>
    client.get(`${INV_BASE}/stock/items/${encodeURIComponent(itemId)}`, { params }).then(unwrap),

  // ── Registers & pending sources ───────────────────────────────
  // Backend mount: GET /purchase-inward-register + GET /purchase-inward/{pending-grns,pending-bills,posted}
  // (see backend/src/routes/erpInventory.js).
  getPurchaseInwardRegister: (params = {}) =>
    client.get(`${INV_BASE}/purchase-inward-register`, { params }).then(unwrap),
  getPendingGrns:            (params = {}) =>
    client.get(`${INV_BASE}/purchase-inward/pending-grns`, { params }).then(unwrap),
  getPendingBills:           (params = {}) =>
    client.get(`${INV_BASE}/purchase-inward/pending-bills`, { params }).then(unwrap),
  getPostedSources:          (params = {}) =>
    client.get(`${INV_BASE}/purchase-inward/posted`, { params }).then(unwrap),

  // ── Preview / post — from GRN ─────────────────────────────────
  // Backend mount: POST /inward/preview-from-grn/:grnId + POST /inward/post-from-grn/:grnId.
  previewFromGrn:            (grnId, payload = {}) =>
    client.post(`${INV_BASE}/inward/preview-from-grn/${encodeURIComponent(grnId)}`, payload).then(unwrap),
  postFromGrn:               (grnId, payload = {}) =>
    client.post(`${INV_BASE}/inward/post-from-grn/${encodeURIComponent(grnId)}`, payload).then(unwrap),

  // ── Preview / post — from Purchase Bill ───────────────────────
  // Backend mount: POST /inward/preview-from-purchase-bill/:purchaseBillId + POST /inward/post-from-purchase-bill/:purchaseBillId.
  previewFromPurchaseBill:   (billId, payload = {}) =>
    client.post(`${INV_BASE}/inward/preview-from-purchase-bill/${encodeURIComponent(billId)}`, payload).then(unwrap),
  postFromPurchaseBill:      (billId, payload = {}) =>
    client.post(`${INV_BASE}/inward/post-from-purchase-bill/${encodeURIComponent(billId)}`, payload).then(unwrap),

  // ── Posting status lookup ─────────────────────────────────────
  getPostingStatus:          (sourceType, sourceId) =>
    client
      .get(`${INV_BASE}/inward/status/${encodeURIComponent(sourceType)}/${encodeURIComponent(sourceId)}`)
      .then(unwrap)
};

export default { inventoryApi };
